import { Box, Button, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import Input from 'components/Common/Input';
import TelephonyMappingIcon from '@/assets/icons/TelephonyMappingIcon';
import SyncIcon from '@/assets/icons/SyncIcon';
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import { MapBusinessState } from '../utils/types';
import AsteriskSyncTab, { AsteriskDrawerFormValues } from './AsteriskSyncTab';

type DrawerTab = 'mapping' | 'sync';

interface Props {
  openState: MapBusinessState | null;
  onClose: () => void;
  onSave: (data: { asteriskAccount: string }) => void;
  isSaving?: boolean;
  form: UseFormReturn<AsteriskDrawerFormValues>;
}

export default function AsteriskMapBusinessDrawer({ openState, onClose, onSave, form }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState<DrawerTab>('mapping');

  const handleSave = form.handleSubmit((data) => onSave(data));

  const handleClose = () => {
    setActiveTab('mapping');
    onClose();
  };

  return (
    <Drawer
      anchor='right'
      open={Boolean(openState)}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 930,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={2.5}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography variant='h6' fontWeight={600}>
          Map Business
        </Typography>
        <IconButton size='small' onClick={handleClose} aria-label='Close'>
          <RxCross2 />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          border: '1px solid #ECECEC',
          borderRadius: 2,
          p: '2px',
          mx: 2.5,
          mt: 2.5,
          mb: 2.5
        }}
      >
        {(
          [
            { id: 'mapping' as const, label: 'Mapping', icon: <TelephonyMappingIcon />, disabled: false },
            { id: 'sync' as const, label: 'Sync', icon: <SyncIcon />, disabled: true }
          ] as const
        ).map((tab) => (
          <Box
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            aria-disabled={tab.disabled}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 1.2,
              borderRadius: 2,
              cursor: tab.disabled ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              transition: 'all .2s ease',
              opacity: tab.disabled ? 0.5 : 1,
              backgroundColor: activeTab === tab.id ? theme.palette.primary.light : 'transparent',
              boxShadow: activeTab === tab.id ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
            }}
          >
            {tab.icon}
            <Typography variant='body2' fontWeight={500}>
              {tab.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box flex={1} overflow='auto' minHeight={0} px={2.5} pb={2.5}>
        {activeTab === 'mapping' ? (
          <Box component='form' display='flex' flexDirection='column' mt={2} gap={3} onSubmit={handleSave}>
            <Input name='businessName' label='Business Name' control={form.control} disabled required />
            <Input name='asteriskAccount' label='Asterisk Account' control={form.control} disabled required />
          </Box>
        ) : (
          <AsteriskSyncTab control={form.control} />
        )}
      </Box>

      <Box
        px={2.5}
        py={2}
        mt='auto'
        display='flex'
        gap={1}
        justifyContent='end'
        borderTop='1px solid'
        borderColor='divider'
      >
        <Button color='secondary' variant='outlined' sx={{ borderRadius: '18px' }} onClick={handleClose}>
          Cancel
        </Button>
      </Box>
    </Drawer>
  );
}
